import { create } from 'zustand';
import { MIN_CHECKOUT_SOURCE_QUANTITY } from '@/features/organizations/billing-constants';

import {
    INITIAL_ONBOARDING_STATE,
    type OnboardingBuildMode,
    type OnboardingCheckoutState,
    type OnboardingConnectedAccount,
    type OnboardingGoalId,
    type OnboardingState,
    type OnboardingStep
} from '@/features/onboarding/data';
import type { OnboardingStateResponse } from '@/features/onboarding/services/onboarding';

export type OnboardingStore = OnboardingState & {
    setStep: (step: OnboardingStep) => void;
    setGoals: (goals: OnboardingGoalId[]) => void;
    toggleGoal: (goal: OnboardingGoalId) => void;
    setBuildMode: (buildMode: OnboardingBuildMode) => void;
    setSources: (sources: number) => void;
    setConnectedAccountCount: (count: number) => void;
    setConnectedAccounts: (accounts: OnboardingConnectedAccount[]) => void;
    setConnectSkipped: (connectSkipped: boolean) => void;
    setCheckout: (checkout: OnboardingCheckoutState) => void;
    markCompleted: (completedAt?: string | null) => void;
    reset: () => void;
};

function clampSources(sources: number): number {
    return Math.max(MIN_CHECKOUT_SOURCE_QUANTITY, Math.min(100, Math.round(sources)));
}

export const useOnboardingStore = create<OnboardingStore>((set) => ({
    ...INITIAL_ONBOARDING_STATE,
    setStep: (step) => set({ step }),
    setGoals: (goals) => set({ goals }),
    toggleGoal: (goal) =>
        set((state) => ({
            goals: state.goals.includes(goal) ? state.goals.filter((item) => item !== goal) : [...state.goals, goal]
        })),
    setBuildMode: (buildMode) => set({ buildMode }),
    setSources: (sources) =>
        set((state) => ({
            sources: Math.max(clampSources(sources), state.connectedAccountCount || 0)
        })),
    setConnectedAccountCount: (count) =>
        set((state) => ({
            connectedAccountCount: Math.max(0, count),
            sources: Math.max(state.sources, Math.max(0, count))
        })),
    setConnectedAccounts: (accounts) =>
        set((state) => ({
            connectedAccounts: accounts,
            connectedAccountCount: accounts.length,
            sources: Math.max(state.sources, accounts.length)
        })),
    setConnectSkipped: (connectSkipped) => set({ connectSkipped }),
    setCheckout: (checkout) => set({ checkout }),
    markCompleted: (completedAt) =>
        set({ checkout: 'completed', completedAt: completedAt ?? new Date().toISOString() }),
    reset: () => set(INITIAL_ONBOARDING_STATE)
}));

let hydratedOrganizationId: string | null = null;

/**
 * Seeds the in-memory onboarding store from the server state.
 *
 * The first sync for an organization is a full hydration (resume support
 * across hard reloads / OAuth+Stripe redirects). Subsequent syncs only merge
 * the fields the server owns authoritatively — connected-account count,
 * the sources floor, and completion — so unsaved local edits (e.g. goals
 * toggled but not yet persisted) are never clobbered by route-guard refetches.
 */
export function syncOnboardingStoreFromServer(organizationId: string, server: OnboardingStateResponse): void {
    if (hydratedOrganizationId === organizationId) {
        useOnboardingStore.setState((state) => ({
            connectedAccountCount: server.connectedAccounts,
            sources: server.completed
                ? Math.max(server.sources, server.connectedAccounts, MIN_CHECKOUT_SOURCE_QUANTITY)
                : Math.max(state.sources, server.connectedAccounts),
            ...(server.completed
                ? { checkout: 'completed' as const, completedAt: server.completedAt ?? state.completedAt }
                : {})
        }));
        return;
    }

    hydratedOrganizationId = organizationId;
    useOnboardingStore.setState({
        step: server.currentStep === 'connect' ? 2 : 1,
        goals: server.goals,
        buildMode: server.buildMode ?? INITIAL_ONBOARDING_STATE.buildMode,
        sources: Math.max(server.sources, server.connectedAccounts, MIN_CHECKOUT_SOURCE_QUANTITY),
        checkout: server.checkoutStatus,
        connectedAccountCount: server.connectedAccounts,
        connectSkipped: server.connectSkipped,
        completedAt: server.completedAt ?? undefined
    });
}

export function resetOnboardingHydration(): void {
    hydratedOrganizationId = null;
}
